import {
  ApolloLink,
  type FetchResult,
  Observable,
  type Observer,
} from '@apollo/client';
import {GraphQLError} from 'graphql';

import {safeSessionStorage} from './util/localStorage';

interface ErrorRule {
  id: string;
  operations: string[]; // Empty array = match all operations
  chance: number; // 0-1, probability of error occurring
  type: 'network' | 'graphql';

  // For network errors
  networkStatusCode?: number;
  networkMessage?: string;

  // For GraphQL errors
  graphqlMessage?: string;
  graphqlCode?: string;
  graphqlPath?: string[];

  matchVariables?: Record<string, any>; // Only apply rule if operation variables match these key-value pairs
  enabled?: boolean; // Rule-specific enable/disable (default true)
}

interface ErrorInjectionConfig {
  enabled: boolean;
  rules: ErrorRule[];
  logInjections?: boolean;
}

const CONFIG_KEY = 'apollo_error_injection_config';

function getConfig(): ErrorInjectionConfig {
  try {
    const stored = safeSessionStorage.getItem(CONFIG_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Failed to parse error injection config:', e);
  }
  return {
    enabled: false,
    rules: [],
    logInjections: true,
  };
}

function saveConfig(config: ErrorInjectionConfig): void {
  try {
    safeSessionStorage.setItem(CONFIG_KEY, JSON.stringify(config));
  } catch (e) {
    console.error('Failed to save error injection config:', e);
  }
}

function generateId(): string {
  return `rule_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Main error injection link
//
// IMPORTANT: On initial page load, the ramp flag check is async and non-blocking.
// This means the first few GraphQL requests will pass through without error injection,
// even if the user has the ramp flag enabled. This is a deliberate trade-off to avoid
// blocking the app startup on a feature flag check.
//
// Once the flag check completes (typically within the first second), the result is cached
// and all subsequent requests will properly inject errors based on the configured rules.
// If you need to test error injection on initial page load, refresh the page and wait
// a moment before triggering the requests you want to test.
export const errorInjectionLink = new ApolloLink((operation, forward) => {
  const config = getConfig();

  if (!config.enabled) {
    return forward(operation);
  }

  const matchingRule = config.rules.find(rule => {
    if (rule.enabled === false) {
      return false;
    }

    if (
      rule.operations.length > 0 &&
      !rule.operations.includes(operation.operationName)
    ) {
      return false;
    }

    if (rule.matchVariables) {
      const matches = Object.entries(rule.matchVariables).every(
        ([key, value]) => operation.variables[key] === value
      );
      if (!matches) {
        return false;
      }
    }

    if (Math.random() > rule.chance) {
      return false;
    }

    return true;
  });

  if (!matchingRule) {
    return forward(operation);
  }

  if (config.logInjections) {
    console.log(
      `[Apollo Error Injector] Injecting ${matchingRule.type} error for ${operation.operationName}`,
      matchingRule
    );
  }

  return new Observable<FetchResult>((observer: Observer<FetchResult>) => {
    const injectError = () => {
      if (matchingRule.type === 'network') {
        // Network errors are passed to observer.error() to trigger error handling
        const error: any = new Error(
          matchingRule.networkMessage ||
            `Network error: ${matchingRule.networkStatusCode}`
        );
        error.statusCode = matchingRule.networkStatusCode || 500;
        error.name = 'NetworkError';
        observer.error?.(error);
      } else if (matchingRule.type === 'graphql') {
        // GraphQL errors are returned as part of the response, not thrown
        observer.next?.({
          data: null,
          errors: [
            new GraphQLError(
              matchingRule.graphqlMessage || 'GraphQL error',
              undefined,
              undefined,
              undefined,
              matchingRule.graphqlPath,
              undefined,
              {code: matchingRule.graphqlCode}
            ),
          ],
        });
        observer.complete?.();
      }
    };

    injectError();
  });
});

// Only expose the console API if the user has the ramp flag enabled.
// This check happens asynchronously after page load to avoid blocking.

(window as any).apolloErrorInjector = {
  enable: () => {
    const config = getConfig();
    config.enabled = true;
    saveConfig(config);
    console.log(`
      🔧 Apollo Error Injector is available!

      Quick start:
      - apolloErrorInjector.status() - Check current configuration
      - apolloErrorInjector.enable() - Enable error injection
      - apolloErrorInjector.addRule({...}) - Add custom error rule

      Example - Make SomeSampleQuery fail 10% of the time:
      apolloErrorInjector.addRule({
        operations: ['SomeSampleQuery'],
        chance: 0.1,
        type: 'network',
        networkStatusCode: 500
      });
      apolloErrorInjector.enable();
      `);
  },

  disable: () => {
    const config = getConfig();
    config.enabled = false;
    saveConfig(config);
    console.log('Apollo error injection disabled');
  },

  status: () => {
    const config = getConfig();
    console.log('Ramp flag enabled:', true);
    console.log('Error injection active:', config.enabled);
    console.log('Rules:', config.rules);
    return {rampFlag: true, enabled: config.enabled, rules: config.rules};
  },

  addRule: (rule: Omit<ErrorRule, 'id'>) => {
    const config = getConfig();
    const id = generateId();
    const newRule = {...rule, id, enabled: rule.enabled ?? true};
    config.rules.push(newRule);
    saveConfig(config);
    console.log(`Rule ${id} added`);
    return id;
  },

  updateRule: (id: string, updates: Partial<ErrorRule>) => {
    const config = getConfig();
    const ruleIndex = config.rules.findIndex(r => r.id === id);
    if (ruleIndex >= 0) {
      config.rules[ruleIndex] = {...config.rules[ruleIndex], ...updates};
      saveConfig(config);
      console.log(`Rule ${id} updated`);
    } else {
      console.error(`Rule ${id} not found`);
    }
  },

  removeRule: (id: string) => {
    const config = getConfig();
    config.rules = config.rules.filter(r => r.id !== id);
    saveConfig(config);
    console.log(`Rule ${id} removed`);
  },

  clearRules: () => {
    const config = getConfig();
    config.rules = [];
    saveConfig(config);
    console.log('All rules cleared');
  },

  listRules: () => {
    const config = getConfig();
    return config.rules;
  },

  presets: {
    graphqlErrors: () => {
      const id = (window as any).apolloErrorInjector.addRule({
        operations: [],
        chance: 1.0,
        type: 'graphql',
        graphqlMessage: 'Test GraphQL error from error injector',
        graphqlCode: 'TEST_ERROR',
      });
      console.log('Configured for GraphQL errors on all requests');
      return id;
    },

    randomErrors: (chance = 0.3) => {
      const id = (window as any).apolloErrorInjector.addRule({
        operations: [],
        chance,
        type: 'network',
        networkStatusCode: 500,
        networkMessage: 'Random server error',
      });
      console.log(`Configured for random errors with ${chance * 100}% chance`);
      return id;
    },

    slowNetwork: (delay = 3000) => {
      const id = (window as any).apolloErrorInjector.addRule({
        operations: [],
        chance: 1.0,
        type: 'network',
        networkStatusCode: 504,
        networkMessage: 'Gateway Timeout',
        delay,
      });
      console.log(`Configured for timeout errors after ${delay}ms`);
      return id;
    },

    specificQuery: (name: string, chance = 1.0) => {
      const id = (window as any).apolloErrorInjector.addRule({
        operations: [name],
        chance,
        type: 'network',
        networkStatusCode: 500,
        networkMessage: `Error in ${name}`,
      });
      console.log(`Configured ${name} to fail with ${chance * 100}% chance`);
      return id;
    },
  },
};
